import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { PhoneOff, Mic, MicOff, Video as VideoIcon, VideoOff, Users } from "lucide-react";
import { WS_BASE } from "@/lib/api";

const ICE = { iceServers: [{ urls: "stun:stun.l.google.com:19302" }] };

function RemoteTile({ stream, id }) {
  const ref = useRef(null);
  useEffect(() => { if (ref.current) ref.current.srcObject = stream; }, [stream]);
  return (
    <div className="relative rounded-2xl overflow-hidden bg-[#121212] border border-[#2A2A2A]" data-testid={`remote-tile-${id}`}>
      <video ref={ref} autoPlay playsInline className="w-full h-full object-cover" />
    </div>
  );
}

export default function EventRoom() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const locState = useLocation().state || {};
  const localRef = useRef(null);
  const localStream = useRef(null);
  const wsRef = useRef(null);
  const peers = useRef({}); // peerId -> RTCPeerConnection
  const myId = useRef(Math.random().toString(36).slice(2));
  const [remotes, setRemotes] = useState({}); // peerId -> MediaStream
  const [status, setStatus] = useState("Connecting…");
  const [micOn, setMicOn] = useState(true);
  const [camOn, setCamOn] = useState(true);

  const send = useCallback((obj) => {
    if (wsRef.current?.readyState === 1) wsRef.current.send(JSON.stringify(obj));
  }, []);

  const setRemote = (peerId, stream) => setRemotes((p) => ({ ...p, [peerId]: stream }));
  const dropRemote = (peerId) => setRemotes((p) => { const n = { ...p }; delete n[peerId]; return n; });

  useEffect(() => {
    let mounted = true;

    const createPeer = (peerId, initiator) => {
      const pc = new RTCPeerConnection(ICE);
      localStream.current.getTracks().forEach((t) => pc.addTrack(t, localStream.current));
      pc.onicecandidate = (e) => { if (e.candidate) send({ type: "candidate", from: myId.current, to: peerId, candidate: e.candidate }); };
      pc.ontrack = (e) => { setRemote(peerId, e.streams[0]); setStatus("Live"); };
      pc.onconnectionstatechange = () => {
        if (["failed", "closed", "disconnected"].includes(pc.connectionState)) { dropRemote(peerId); }
      };
      peers.current[peerId] = pc;
      if (initiator) {
        pc.createOffer()
          .then((o) => pc.setLocalDescription(o))
          .then(() => send({ type: "offer", from: myId.current, to: peerId, sdp: pc.localDescription }));
      }
      return pc;
    };

    const discover = (peerId) => {
      if (peerId === myId.current || peers.current[peerId]) return;
      createPeer(peerId, myId.current > peerId);
    };

    const start = async () => {
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      } catch (e) { setStatus("Camera/mic permission denied"); return; }
      if (!mounted) { stream.getTracks().forEach((t) => t.stop()); return; }
      localStream.current = stream;
      if (localRef.current) localRef.current.srcObject = stream;

      const ws = new WebSocket(`${WS_BASE}/event/${eventId}?token=${localStorage.getItem("pe_token")}`);
      wsRef.current = ws;

      ws.onopen = () => { setStatus("Waiting for others…"); send({ type: "join", from: myId.current }); };
      ws.onclose = (e) => { if (e.code === 4403) setStatus("RSVP required to join this event"); };
      ws.onmessage = async (evt) => {
        const msg = JSON.parse(evt.data);
        if (msg.from === myId.current) return;
        if (msg.type === "join") {
          send({ type: "hello", from: myId.current, to: msg.from });
          discover(msg.from);
        } else if (msg.type === "hello") {
          if (msg.to === myId.current) discover(msg.from);
        } else if (msg.type === "offer") {
          if (msg.to !== myId.current) return;
          const pc = peers.current[msg.from] || createPeer(msg.from, false);
          await pc.setRemoteDescription(new RTCSessionDescription(msg.sdp));
          const ans = await pc.createAnswer();
          await pc.setLocalDescription(ans);
          send({ type: "answer", from: myId.current, to: msg.from, sdp: pc.localDescription });
        } else if (msg.type === "answer") {
          if (msg.to !== myId.current) return;
          await peers.current[msg.from]?.setRemoteDescription(new RTCSessionDescription(msg.sdp));
        } else if (msg.type === "candidate") {
          if (msg.to !== myId.current) return;
          try { await peers.current[msg.from]?.addIceCandidate(new RTCIceCandidate(msg.candidate)); } catch (e) {}
        } else if (msg.type === "leave") {
          peers.current[msg.from]?.close();
          delete peers.current[msg.from];
          dropRemote(msg.from);
        }
      };
    };

    start();
    return () => {
      mounted = false;
      send({ type: "leave", from: myId.current });
      wsRef.current?.close();
      Object.values(peers.current).forEach((pc) => pc.close());
      peers.current = {};
      localStream.current?.getTracks().forEach((t) => t.stop());
    };
  }, [eventId, send]);

  const toggleMic = () => { const t = localStream.current?.getAudioTracks()[0]; if (t) { t.enabled = !t.enabled; setMicOn(t.enabled); } };
  const toggleCam = () => { const t = localStream.current?.getVideoTracks()[0]; if (t) { t.enabled = !t.enabled; setCamOn(t.enabled); } };
  const leave = () => {
    send({ type: "leave", from: myId.current });
    wsRef.current?.close();
    Object.values(peers.current).forEach((pc) => pc.close());
    localStream.current?.getTracks().forEach((t) => t.stop());
    navigate("/events");
  };

  const remoteIds = Object.keys(remotes);
  const count = remoteIds.length + 1;
  const cols = count <= 1 ? "grid-cols-1" : count <= 4 ? "grid-cols-2" : "grid-cols-3";

  return (
    <div className="fixed inset-0 z-50 bg-black flex flex-col">
      <header className="flex items-center justify-between px-6 py-4 border-b border-[#2A2A2A]">
        <div>
          <div className="overline">Live Lounge</div>
          <div className="font-heading text-2xl gold-text">{locState.title || "Empire Event"}</div>
        </div>
        <div className="flex items-center gap-2 text-sm text-[#A0A0A0]">
          <Users size={16} className="text-[#D4AF37]" /> {count} in room · <span className="text-[#D4AF37]">{status}</span>
        </div>
      </header>

      <div className={`flex-1 grid ${cols} gap-3 p-4 overflow-y-auto`}>
        <div className="relative rounded-2xl overflow-hidden border border-[#D4AF37]/60 bg-[#121212]" data-testid="local-tile">
          <video ref={localRef} autoPlay playsInline muted className="w-full h-full object-cover" />
          <span className="absolute bottom-2 left-2 text-xs px-2 py-0.5 rounded-full bg-black/60 text-white">You</span>
        </div>
        {remoteIds.map((id) => <RemoteTile key={id} id={id} stream={remotes[id]} />)}
      </div>

      <div className="flex items-center justify-center gap-4 py-5 border-t border-[#2A2A2A]">
        <button data-testid="toggle-mic" onClick={toggleMic}
          className={`w-12 h-12 rounded-full flex items-center justify-center ${micOn ? "bg-white/10 text-white" : "bg-white text-black"}`}>
          {micOn ? <Mic size={20} /> : <MicOff size={20} />}
        </button>
        <button data-testid="leave-room" onClick={leave}
          className="w-16 h-16 rounded-full bg-[#E63946] text-white flex items-center justify-center hover:brightness-110">
          <PhoneOff size={26} />
        </button>
        <button data-testid="toggle-cam" onClick={toggleCam}
          className={`w-12 h-12 rounded-full flex items-center justify-center ${camOn ? "bg-white/10 text-white" : "bg-white text-black"}`}>
          {camOn ? <VideoIcon size={20} /> : <VideoOff size={20} />}
        </button>
      </div>
    </div>
  );
}
